import { useTranslation } from "react-i18next";
import { History } from "lucide-react";
import { formatDateTime } from "../../lib/format";
import { Button } from "../../components/ui/button";
import type { DraftSaveStatus } from "./draft-saver";
import type { OfferedDraft } from "./use-input-draft";

/**
 * The two surfaces [NF-022] adds to a mask, shared by the Erfassungsmaske and
 * the Postmortem-Maske. `mask` picks the handles: each `data-testid` below is
 * a ternary over two literals, so a grep for either lands here.
 *
 * Wording keeps "Entwurf" out on purpose: in the Postmortem-Maske that word is
 * the F-007 state somebody saved deliberately, and this is the opposite -
 * input nobody has saved yet.
 */

export type DraftMask = "incident-create" | "postmortem";

/** "Unsaved input found" with Wiederherstellen / Verwerfen ([AC-030]). */
export function DraftRestoreBanner({
  mask,
  offered,
  onRestore,
  onDiscard,
}: {
  mask: DraftMask;
  offered: OfferedDraft;
  onRestore: () => void;
  onDiscard: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const create = mask === "incident-create";
  return (
    <div
      role="region"
      aria-label={t("drafts.bannerTitle")}
      data-testid={create ? "incident-create_draft-banner" : "postmortem_draft-banner"}
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-950 dark:border-sky-700 dark:bg-sky-950/40 dark:text-sky-50"
    >
      <span className="flex min-w-0 items-start gap-2">
        <History aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          <span className="block font-medium">{t("drafts.bannerTitle")}</span>
          <span
            data-testid={create ? "incident-create_draft-banner-text" : "postmortem_draft-banner-text"}
            className="block"
          >
            {t("drafts.bannerText", { at: formatDateTime(offered.at) })}
          </span>
        </span>
      </span>
      <span className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          data-testid={create ? "incident-create_draft-restore" : "postmortem_draft-restore"}
          onClick={onRestore}
        >
          {t("drafts.restore")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid={create ? "incident-create_draft-discard" : "postmortem_draft-discard"}
          onClick={onDiscard}
        >
          {t("drafts.discard")}
        </Button>
      </span>
    </div>
  );
}

/**
 * Where the autosave stands. Quiet while it works; a failure is announced,
 * because then the user is the one who can still prevent a loss.
 */
export function DraftSaveStatusLine({
  mask,
  status,
  savedAt,
}: {
  mask: DraftMask;
  status: DraftSaveStatus;
  savedAt: number | null;
}): JSX.Element | null {
  const { t } = useTranslation();
  const create = mask === "incident-create";
  if (status === "idle") return null;
  const failing = status === "retrying" || status === "failed";
  const text =
    status === "saved" && savedAt !== null
      ? t("drafts.statusSaved", { at: formatDateTime(new Date(savedAt).toISOString()) })
      : status === "retrying"
        ? t("drafts.statusRetrying")
        : status === "failed"
          ? t("drafts.statusFailed")
          : t("drafts.statusSaving");
  return (
    <p
      data-testid={create ? "incident-create_draft-status" : "postmortem_draft-status"}
      role={failing ? "status" : undefined}
      className={failing ? "text-xs font-medium text-destructive-text" : "text-xs text-muted-foreground"}
    >
      {text}
    </p>
  );
}
